// components/analytics/ReportChart.jsx — SOUND-599
//
// Optional chart over the Auth Performance report builder's current result set.
// Draws from the ALREADY-FETCHED /report rows — no extra request.
//
// The chart types on offer are derived from the shape of the report, not fixed:
// a time series is only offered when a date dimension is grouped, and a rate can
// never be stacked or turned into a donut. All of that logic (and the rule that
// every collapse recomputes the rate from summed counts rather than averaging)
// lives in utilities/reportChart.js, which is unit-tested. This file renders.
import React, { useMemo, useState, useEffect } from "react";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import clsx from "clsx";
import { BarChart3, ChevronDown, Info } from "lucide-react";
import {
  CHART_TYPES, MEASURES, OTHER,
  aggregateForChart, canStack, resolveChartType, defaultAxes,
  toDonutSlices, measureLabel, measureUnit,
} from "utilities/reportChart";

const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(2)}%`);
const fmtCompact = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : fmtInt(v));

function cssVar(name, alpha = 1) {
  if (typeof window === "undefined") return "";
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return "";
  const [h, s, l] = raw.split(/\s+/);
  return `hsla(${parseFloat(h)}, ${s}, ${l}, ${alpha})`;
}

// Distinguishable in both themes. OTHER is deliberately muted — it is a residual,
// not a category, and should not compete with real series for attention.
const PALETTE = [
  "hsl(146 70% 45%)", "hsl(217 91% 60%)", "hsl(280 65% 60%)", "hsl(31 92% 55%)",
  "hsl(190 85% 42%)", "hsl(340 75% 58%)", "hsl(85 60% 45%)", "hsl(255 70% 65%)",
  "hsl(15 80% 55%)", "hsl(200 60% 50%)",
];
const OTHER_COLOR = "hsl(215 15% 55%)";
const colorFor = (key, i) => (key === OTHER ? OTHER_COLOR : PALETTE[i % PALETTE.length]);

function Seg({ value, onChange, options, disabledTitle }) {
  return (
    <div className="inline-flex rounded-lg ring-1 ring-border overflow-hidden">
      {options.map((o) => {
        const disabled = o.disabled === true;
        return (
          <button
            key={String(o.value)}
            type="button"
            disabled={disabled}
            aria-pressed={value === o.value}
            title={disabled ? o.reason || disabledTitle : o.title}
            onClick={() => !disabled && onChange(o.value)}
            className={clsx(
              "px-2.5 py-1.5 text-xs whitespace-nowrap",
              disabled
                ? "text-muted-foreground/40 cursor-not-allowed"
                : value === o.value
                ? "bg-brand/15 text-foreground"
                : "text-muted-foreground hover:bg-muted/60"
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function DimSelect({ label, value, onChange, options, allowNone }) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {label}
      <div className="relative">
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          className="appearance-none surface rounded-md ring-1 ring-border pl-2 pr-6 py-1 text-xs text-foreground"
        >
          {allowNone && <option value="">None</option>}
          {options.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
      </div>
    </label>
  );
}

// Always shows the counts behind a rate. A rate on 3 attempts is noise, and making
// n visible everywhere is cheaper and more honest than a sample-floor control.
function ChartTooltip({ active, payload, label, measure }) {
  if (!active || !payload?.length) return null;
  const cells = payload[0]?.payload?.__cells || {};
  const showRate = measure === "auth_rate";
  return (
    <div
      className="rounded-md px-2.5 py-2 text-xs shadow-lg"
      style={{ background: cssVar("--card", 0.98), border: `1px solid ${cssVar("--border", 1)}` }}
    >
      <div className="font-medium text-foreground mb-1">{label}</div>
      {payload.map((p) => {
        const c = cells[p.dataKey] || {};
        return (
          <div key={p.dataKey} className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-sm" style={{ background: p.color }} />
            <span className="text-muted-foreground">{p.dataKey}</span>
            <span className="text-foreground ml-auto tabular-nums">
              {showRate ? fmtPct(p.value) : fmtInt(p.value)}
            </span>
            {c.attempts !== undefined && (
              <span className="text-muted-foreground/70 tabular-nums">
                {showRate
                  ? `(${fmtInt(c.approved)}/${fmtInt(c.attempts)})`
                  : `· ${fmtPct(c.rate)}`}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ReportChart({
  rows = [],
  groupBy = [],
  dateDims,
  dimLabel = (k) => k,
  truncated = false,
  totalGroups = 0,
  maxSeries = 10,
  onHide,
}) {
  const dateSet = useMemo(() => dateDims || new Set(), [dateDims]);

  const [measure, setMeasure] = useState("auth_rate");
  const [requestedType, setRequestedType] = useState("line");
  const [axes, setAxes] = useState(() => defaultAxes({ groupBy, dateDims: dateSet }));
  const [stacked, setStacked] = useState(false);
  const [horizontal, setHorizontal] = useState(false);

  // Re-derive the axes whenever the grouping changes — a stale xDim that is no
  // longer in groupBy would silently chart nothing.
  const groupKey = groupBy.join("|");
  useEffect(() => {
    setAxes(defaultAxes({ groupBy, dateDims: dateSet }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey]);

  const xDim = groupBy.includes(axes.xDim) ? axes.xDim : defaultAxes({ groupBy, dateDims: dateSet }).xDim;
  const seriesDim = groupBy.includes(axes.seriesDim) && axes.seriesDim !== xDim ? axes.seriesDim : null;

  const resolved = useMemo(
    () => resolveChartType({ requested: requestedType, groupBy, measure, dateDims: dateSet }),
    [requestedType, groupBy, measure, dateSet]
  );
  const chartType = resolved.type;
  const capability = resolved.capability;
  const stackable = useMemo(() => canStack({ groupBy, measure }), [groupBy, measure]);
  const stackOn = stacked && stackable.available && chartType === "bar";

  const agg = useMemo(
    () =>
      aggregateForChart({
        rows, groupBy, xDim, seriesDim, measure, dateDims: dateSet, maxSeries,
      }),
    [rows, groupBy, xDim, seriesDim, measure, dateSet, maxSeries]
  );

  if (!groupBy.length || !rows.length || !chartType) return null;

  const isRate = measure === "auth_rate";
  const grid = cssVar("--border", 0.5);
  const axisTick = { fontSize: 11 };
  const valueFmt = isRate ? (v) => fmtPct(v) : fmtCompact;

  const notes = [];
  if (resolved.fellBack && resolved.reason) notes.push(resolved.reason);
  if (agg.collapsedDims.length) {
    notes.push(
      `${agg.collapsedDims.map(dimLabel).join(", ")} ${agg.collapsedDims.length === 1 ? "is" : "are"} not on an axis — ` +
        `${agg.collapsedDims.length === 1 ? "its" : "their"} rows are summed into each point${isRate ? ", and the rate recomputed" : ""}.`
    );
  }
  if (agg.foldedSeries > 0) notes.push(`${agg.foldedSeries} smaller series folded into "${OTHER}" by volume.`);
  if (agg.foldedCategories > 0) notes.push(`${agg.foldedCategories} smaller categories folded into "${OTHER}".`);

  const measureOptions = MEASURES.map((m) => ({ value: m.value, label: m.label }));
  const typeOptions = CHART_TYPES.map((t) => ({
    value: t.value,
    label: t.label,
    disabled: !capability[t.value]?.available,
    reason: capability[t.value]?.reason,
  }));
  const axisDims = groupBy.map((k) => ({ key: k, label: dimLabel(k) }));

  return (
    <div className="surface rounded-xl ring-1 ring-border p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <BarChart3 className="h-4 w-4" /> Chart
        </h3>
        <Seg value={chartType} onChange={setRequestedType} options={typeOptions} />
        <Seg value={measure} onChange={setMeasure} options={measureOptions} />

        {chartType !== "donut" && (
          <>
            <DimSelect
              label="X"
              value={xDim}
              onChange={(v) => setAxes((a) => ({ ...a, xDim: v }))}
              options={axisDims}
            />
            <DimSelect
              label="Series"
              value={seriesDim}
              onChange={(v) => setAxes((a) => ({ ...a, seriesDim: v }))}
              options={axisDims.filter((d) => d.key !== xDim)}
              allowNone
            />
          </>
        )}

        {chartType === "bar" && (
          <div className="flex items-center gap-3">
            <label
              className={clsx("flex items-center gap-1.5 text-xs", stackable.available ? "text-muted-foreground" : "text-muted-foreground/40")}
              title={stackable.reason || undefined}
            >
              <input
                type="checkbox"
                checked={stackOn}
                disabled={!stackable.available}
                onChange={(e) => setStacked(e.target.checked)}
                className="rounded border-border"
              />
              Stacked
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={horizontal}
                onChange={(e) => setHorizontal(e.target.checked)}
                className="rounded border-border"
              />
              Horizontal
            </label>
          </div>
        )}

        {onHide && (
          <button
            type="button"
            onClick={onHide}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground"
          >
            Hide
          </button>
        )}
      </div>

      {/* Truncation is stated on the chart itself, not only next to the table. A
          chart drawn on a server-capped result set otherwise looks complete. */}
      {truncated && (
        <div className="flex items-start gap-2 rounded-md bg-amber-500/10 ring-1 ring-amber-500/30 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Charting the top {fmtInt(rows.length)} of {fmtInt(totalGroups)} groups by attempts — the
            server capped the result set. Narrow the range or filters for a complete picture.
          </span>
        </div>
      )}

      <ResponsiveContainer width="100%" height={340}>
        {chartType === "donut" ? (
          <PieChart>
            <Tooltip
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <div
                    className="rounded-md px-2.5 py-2 text-xs shadow-lg"
                    style={{ background: cssVar("--card", 0.98), border: `1px solid ${cssVar("--border", 1)}` }}
                  >
                    <div className="font-medium text-foreground">{payload[0].name}</div>
                    <div className="text-muted-foreground">
                      {fmtInt(payload[0].value)} {measureUnit(measure) === "count" ? measureLabel(measure).toLowerCase() : ""}
                      {" · "}
                      {fmtPct(payload[0].payload.rate)} auth rate
                    </div>
                  </div>
                ) : null
              }
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Pie
              data={toDonutSlices({ data: agg.data, measure })}
              dataKey="value"
              nameKey="name"
              innerRadius="55%"
              outerRadius="80%"
              paddingAngle={1}
              isAnimationActive={false}
            >
              {toDonutSlices({ data: agg.data, measure }).map((s, i) => (
                <Cell key={s.name} fill={colorFor(s.name, i)} />
              ))}
            </Pie>
          </PieChart>
        ) : (
          <ComposedChart
            data={agg.data}
            layout={horizontal && chartType === "bar" ? "vertical" : "horizontal"}
            margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={grid} />
            {horizontal && chartType === "bar" ? (
              <>
                <XAxis type="number" tick={axisTick} tickFormatter={valueFmt} />
                <YAxis type="category" dataKey="x" tick={axisTick} width={130} interval={0} />
              </>
            ) : (
              <>
                <XAxis dataKey="x" tick={axisTick} minTickGap={20} />
                <YAxis
                  yAxisId="main"
                  tick={axisTick}
                  width={isRate ? 44 : 56}
                  domain={isRate ? [0, 100] : undefined}
                  tickFormatter={valueFmt}
                />
                {chartType === "combo" && (
                  <YAxis
                    yAxisId="vol"
                    orientation="right"
                    tick={axisTick}
                    width={56}
                    tickFormatter={fmtCompact}
                  />
                )}
              </>
            )}
            <Tooltip content={<ChartTooltip measure={measure} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />

            {/* Volume context behind the rate — the same idiom as the page's
                "Auth Rate over time" card, generalised to any grouping. */}
            {chartType === "combo" && (
              <Bar
                yAxisId="vol"
                name="Attempts"
                dataKey="__attempts"
                fill={cssVar("--muted-foreground", 0.28) || "hsla(215,15%,55%,0.28)"}
                isAnimationActive={false}
              />
            )}

            {agg.seriesKeys.map((key, i) =>
              chartType === "bar" ? (
                <Bar
                  key={key}
                  {...(horizontal ? {} : { yAxisId: "main" })}
                  name={key}
                  dataKey={key}
                  stackId={stackOn ? "s" : undefined}
                  fill={colorFor(key, i)}
                  isAnimationActive={false}
                />
              ) : (
                <Line
                  key={key}
                  yAxisId="main"
                  name={key}
                  type="monotone"
                  dataKey={key}
                  stroke={colorFor(key, i)}
                  strokeWidth={2}
                  dot={false}
                  // A null value is a GAP (no attempts), not a zero. connectNulls
                  // would draw straight through it and imply data we do not have.
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )
            )}
          </ComposedChart>
        )}
      </ResponsiveContainer>

      {notes.length > 0 && (
        <ul className="space-y-1">
          {notes.map((n) => (
            <li key={n} className="text-[11px] text-muted-foreground flex items-start gap-1.5">
              <Info className="h-3 w-3 mt-0.5 shrink-0" />
              <span>{n}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
